'use client';

import { useMemo } from 'react';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';

interface FilterSetFilter {
  id: number;
  filter_set: number;
  type: string;
  is_exclude: boolean;
  is_all: boolean;
  values: string[];
}

interface FilterSetData {
  id: string;
  label: string;
  type: string;
  filters?: FilterSetFilter[];
  detailed_filters?: FilterSetFilter[];
}

const IGNORED_FILTER_TYPES = ['PAYER_TYPE'];

/**
 * Hook to check if a selected filter set contains filter types that will be ignored
 * for Fresh Faces strategies (PAYER_TYPE).
 *
 * @param selectedFilterSetId - The ID of the currently selected filter set
 * @returns Object containing:
 *   - hasIgnoredFilters: boolean indicating if ignored filter types are present
 *   - ignoredFilterTypes: array of ignored filter type strings found
 *   - isLoading: boolean indicating if filter set data is still loading
 *
 * @example
 * const { hasIgnoredFilters, ignoredFilterTypes } = useFilterSetWarning('123');
 * if (hasIgnoredFilters) {
 *   console.log('Warning: these filters will be ignored:', ignoredFilterTypes);
 * }
 */
export const useFilterSetWarning = (selectedFilterSetId?: string | null) => {
  const { data: filterSetsData, isLoading } = useGetFilterSetsQuery({
    serializer: 'detailed',
  });

  const ignoredFilterTypes = useMemo(() => {
    if (!selectedFilterSetId || !filterSetsData?.results) {
      return [];
    }

    // Find the selected filter set
    const selectedFilterSet = filterSetsData.results.find(
      (fs) => String(fs.id) === String(selectedFilterSetId)
    );

    if (!selectedFilterSet) {
      return [];
    }

    // Collect all filter types from both filters and detailed_filters arrays
    const allFilterTypes = new Set<string>();

    if (selectedFilterSet.filters) {
      selectedFilterSet.filters.forEach((filter: FilterSetFilter) => {
        if (filter.type) {
          allFilterTypes.add(filter.type);
        }
      });
    }

    if (selectedFilterSet.detailed_filters) {
      selectedFilterSet.detailed_filters.forEach((filter: FilterSetFilter) => {
        if (filter.type) {
          allFilterTypes.add(filter.type);
        }
      });
    }

    // Filter to only include ignored types
    return Array.from(allFilterTypes).filter((type) =>
      IGNORED_FILTER_TYPES.includes(type)
    );
  }, [selectedFilterSetId, filterSetsData?.results]);

  return {
    hasIgnoredFilters: ignoredFilterTypes.length > 0,
    ignoredFilterTypes,
    isLoading,
  };
};
